import { useCallback, useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useSaveFlow } from '../../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo } from '../../../components/hotkeys';
import { BusyIcon } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { guardedExit, resetUnsavedTracking, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import {
  RC_BASE, RC_FORM_NAME, RC_PAGE_ID, SAMPLING_TYPES,
  changedRows, copyToRows, filterRcRows, toRcRow, withRoute,
  type RcRoute, type RcRow,
} from './routeConfiguration';
import './routeConfiguration.css';

/**
 * Sampling Management ▸ Route Configuration (AQT design note, 2026-10-23).
 *
 * One list of the active Pre-Login COCs. Route ID and Sampling Type are edited
 * in the grid, or copied from one cell to every ticked COC with the right-click
 * **Copy To All Cell**;
 * **Save** stores all the changes in one transaction. It edits the same
 * <c>dbo.COCSettings</c> rows as Settings ▸ Pre-Login Settings, so the two forms
 * always show the same values. Existing COCs keep a blank Sampling Type until
 * someone picks one.
 */
export function RouteConfigurationPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const perm = useNavActions(RC_PAGE_ID);

  const [rows, setRows] = useState<RcRow[]>([]);
  const [loaded, setLoaded] = useState<Map<string, RcRow>>(new Map());
  const [routes, setRoutes] = useState<RcRoute[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  // Right-click menu on an editable cell ("Copy To All Cell").
  const [cellMenu, setCellMenu] = useState<{ x: number; y: number; row: RcRow; field: 'route' | 'samplingType' } | null>(null);

  const changed = useMemo(() => changedRows(rows, loaded), [rows, loaded]);
  const dirty = changed.length > 0;
  // Rule 15: the grid buffer is state the automatic tracker cannot see.
  useUnsavedGuard(dirty);
  const canWrite = perm.write !== false;

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const res = await api.get<{ data?: Record<string, unknown>[]; routes?: RcRoute[] }>(RC_BASE);
      const next = (res.data?.data ?? []).map(toRcRow);
      setRows(next);
      setLoaded(new Map(next.map((r) => [r.key, r])));
      setRoutes(res.data?.routes ?? []);
      setSelected(new Set());
      resetUnsavedTracking();
    } catch {
      notify.error('The COCs could not be loaded. Check that the server is running, then press Refresh.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const routeByCode = useMemo(() => new Map(routes.map((r) => [r.routeCode ?? '', r])), [routes]);
  const routeCodes = useMemo(() => routes.map((r) => r.routeCode ?? '').filter(Boolean), [routes]);

  const patch = (key: string, fn: (r: RcRow) => RcRow) =>
    setRows((d) => d.map((r) => (r.key === key ? fn(r) : r)));

  const commitCell = (row: RcRow, field: string, v: string) => {
    if (field === 'routeCode') patch(row.key, (r) => withRoute(r, v ? routeByCode.get(v) ?? null : null));
    else if (field === 'samplingType') patch(row.key, (r) => ({ ...r, samplingType: v }));
  };

  // ── Save: every changed COC, one transaction ─────────────────────────────────
  const save = () => {
    if (!dirty || saving) return;
    void runSave({
      formName: RC_FORM_NAME,
      auditAction: 'edit',
      save: async () => {
        setSaving(true);
        try { await api.post(`${RC_BASE}/save`, { rows: changed }); }
        finally { setSaving(false); }
      },
      onSaved: () => { void load(); },
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
        notify.error(msg ?? 'The route configuration could not be saved.');
      },
    });
  };

  /** Right-click on an editable cell opens "Copy To All Cell". */
  const openCellMenu = (field: 'route' | 'samplingType') =>
    (e: ReactMouseEvent<HTMLTableCellElement>, r: RcRow) => {
      if (!canWrite) return;
      e.preventDefault();
      setCellMenu({ x: e.clientX, y: e.clientY, row: r, field });
    };

  /** The clicked cell's value onto the TICKED rows only, as Prep Batch / Sample
   *  Registration do (G5 "Copy To All Cell" is a misnomer for selected rows).
   *  A ticked row hidden by the search is left alone. */
  const copyToAll = () => {
    if (!cellMenu) return;
    const { row, field } = cellMenu;
    setCellMenu(null);
    const keys = new Set(shown.filter((r) => selected.has(r.key)).map((r) => r.key));
    if (keys.size === 0) { notify.warning('Select at least one row to copy to.'); return; }
    setRows((d) => copyToRows(d, keys, row, field));
    const what = field === 'route' ? 'Route ID' : 'Sampling Type';
    notify.info(`${what} copied to ${keys.size} ${keys.size === 1 ? 'COC' : 'COCs'} — press Save to store.`);
  };

  const refresh = () => { if (dirty) void guardedExit(() => { void load(); }); else void load(); };

  // Save is a form-specific button, so its key is registered here (rule 14).
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'List', order: 1, run: () => save(), disabled: !dirty || saving || !canWrite },
  ]);

  const columns: AppColumn<RcRow>[] = useMemo(() => [
    { field: 'cocId', header: 'COCID', width: 110, minWidth: 100 },
    { field: 'projectRef', header: 'ProjectID', width: 200, minWidth: 140 },
    { field: 'projectName', header: 'ProjectName', width: 200, minWidth: 140 },
    { field: 'projectCode', header: 'ProjectCode', width: 180, minWidth: 120 },
    { field: 'client', header: 'Client', width: 200, minWidth: 140 },
    { field: 'routeCode', header: 'Route ID', width: 120, minWidth: 110,
      edit: { type: 'combo', options: routeCodes, clearable: true, disabled: () => !canWrite },
      onCellContextMenu: openCellMenu('route') },
    { field: 'routeName', header: 'Route Name', width: 180, minWidth: 140 },
    { field: 'samplingType', header: 'SamplingType', width: 140, minWidth: 130,
      edit: { type: 'combo', options: [...SAMPLING_TYPES], clearable: true, disabled: () => !canWrite },
      onCellContextMenu: openCellMenu('samplingType') },
    // openCellMenu reads only stable setters and canWrite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [routeCodes, canWrite]);

  const shown = useMemo(() => filterRcRows(rows, search), [rows, search]);
  const changedKeys = useMemo(() => new Set(changed.map((c) => c.oid)), [changed]);

  return (
    // Same page structure as Sample Prep Queue: header (breadcrumb + title) on top,
    // then toolbar + search on the grey page, and ONE white card holding the grid.
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Sampling Management</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Route Configuration</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId={RC_PAGE_ID} loadMs={loadMs} loading={loading} auditFormName={RC_FORM_NAME} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div className="mlv-toolbar-row">
          <AppToolbar
            onRefresh={refresh}
            refreshing={loading}
            left={(
              <button
                className="ca-btn ca-btn-solid"
                onClick={save}
                disabled={!dirty || saving || !canWrite}
                title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}
              >
                {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
              </button>
            )}
            exportData={{ filename: 'Route Configuration', ...gridExportMatrix(shown, columns) }}
          />
        </div>

        <GridSearch value={search} onChange={setSearch} placeholder="Search..." />

        <div className="bl-card bl-card-flex">
          <AppDataGrid<RcRow>
            rows={shown}
            columns={columns}
            getRowId={(r) => r.key}
            loading={loading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onCellCommit={(row, col, v) => commitCell(row, col.field as string, v ?? '')}
            getRowClassName={(r) => (changedKeys.has(r.key) ? 'rc-row-changed' : undefined)}
            defaultPageSize={20}
            exportFilename="Route Configuration"
            emptyText={search ? 'No results match your search.' : 'No active COCs.'}
          />
        </div>
      </div>

      {cellMenu && (
        <>
          <div className="rc-menu-backdrop" onClick={() => setCellMenu(null)}
               onContextMenu={(e) => { e.preventDefault(); setCellMenu(null); }} />
          <div className="rc-menu" role="menu" style={{ left: cellMenu.x, top: cellMenu.y }}
               onKeyDown={(e) => { if (e.key === 'Escape') setCellMenu(null); }}>
            <button type="button" role="menuitem" autoFocus onClick={copyToAll}>
              <i className="ti ti-copy" aria-hidden="true" /> Copy To All Cell
            </button>
          </div>
        </>
      )}
    </div>
  );
}
